/**
 * Geometry measurements for multi-lane maps (M66).
 *
 * Answers the questions a lane layout has to survive before it is worth
 * balancing: how long is each lane, how much buildable room is left, and how
 * many 2×2 anchors can actually reach more than one lane at once.
 */

import { footprintCells, footprintCenter } from '../model/towers'
import { normalizeMapRoutes, routeTrack } from '../model/path'
import type { GridPosition, MapDefinition, RouteMovementClass } from '../model/types'

/** LV1 range of the cheap single-target towers (fire / frost / poison). */
export const SMALL_TOWER_RANGE = 4.5

const FOOTPRINT_2X2 = { width: 2, height: 2 } as const

export interface LaneRouteMetrics {
  id: string
  movementClass: RouteMovementClass
  spawn: GridPosition
  exit: GridPosition
  /** Authored waypoints (cell-by-cell for ground lanes). */
  cells: number
  /** Track length in cell units. */
  length: number
  /** Direction changes along the lane. */
  turns: number
}

export interface LaneCoverageMetrics {
  /** Reference range used for every coverage count. */
  range: number
  /** 2×2 anchors whose four cells are all grass. */
  anchors: number
  /** Anchors in range of at least one cell of each ground lane. */
  anchorsPerRoute: Record<string, number>
  /** Anchors reaching exactly one ground lane. */
  anchorsSingleLane: number
  /** Anchors reaching two or more ground lanes. */
  anchorsMultiLane: number
  /** Anchors reaching every ground lane on the map. */
  anchorsAllLanes: number
  /** Anchors reaching every ground lane and the air lane. */
  anchorsAllRoutes: number
  /** The all-ground-lane anchors themselves — the premium central plots. */
  premiumAnchors: GridPosition[]
}

export interface LaneMapMetrics {
  id: string
  name: string
  width: number
  height: number
  /** PATH tiles painted on the grid (unique cells; routes may share cells). */
  pathTiles: number
  /** Grass tiles — the raw buildable surface before footprint rules. */
  buildableCells: number
  groundRoutes: number
  airRoutes: number
  routes: LaneRouteMetrics[]
  coverage: LaneCoverageMetrics
}

function countTurns(cells: GridPosition[]): number {
  let turns = 0
  for (let i = 2; i < cells.length; i += 1) {
    const a = cells[i - 2]!
    const b = cells[i - 1]!
    const c = cells[i]!
    const sameAxis = (a.x === b.x && b.x === c.x) || (a.y === b.y && b.y === c.y)
    if (!sameAxis) turns += 1
  }
  return turns
}

function withinRange(center: { x: number; y: number }, cell: GridPosition, range: number): boolean {
  const dx = center.x - (cell.x + 0.5)
  const dy = center.y - (cell.y + 0.5)
  return Math.hypot(dx, dy) <= range
}

/** Grass-only 2×2 anchors, in row-major order. */
export function validAnchors2x2(map: MapDefinition): GridPosition[] {
  const anchors: GridPosition[] = []
  for (let y = 0; y <= map.height - FOOTPRINT_2X2.height; y += 1) {
    for (let x = 0; x <= map.width - FOOTPRINT_2X2.width; x += 1) {
      const cells = footprintCells({ x, y }, FOOTPRINT_2X2)
      if (cells.every((cell) => map.rows[cell.y]?.[cell.x] === 'G')) anchors.push({ x, y })
    }
  }
  return anchors
}

export function measureLaneMap(
  map: MapDefinition,
  range = SMALL_TOWER_RANGE
): LaneMapMetrics {
  const routes = normalizeMapRoutes(map)
  const ground = routes.filter((route) => route.movementClass === 'GROUND')
  const air = routes.filter((route) => route.movementClass === 'AIR')

  let pathTiles = 0
  let buildableCells = 0
  for (let y = 0; y < map.height; y += 1) {
    for (let x = 0; x < map.width; x += 1) {
      if (map.rows[y]![x] === 'P') pathTiles += 1
      else buildableCells += 1
    }
  }

  const anchors = validAnchors2x2(map)
  const anchorsPerRoute: Record<string, number> = {}
  for (const route of routes) anchorsPerRoute[route.id] = 0

  let anchorsSingleLane = 0
  let anchorsMultiLane = 0
  let anchorsAllLanes = 0
  let anchorsAllRoutes = 0
  const premiumAnchors: GridPosition[] = []

  for (const anchor of anchors) {
    const center = footprintCenter(anchor, FOOTPRINT_2X2)
    const reached = routes.filter((route) =>
      route.cells.some((cell) => withinRange(center, cell, range))
    )
    for (const route of reached) anchorsPerRoute[route.id]! += 1

    const groundReached = reached.filter((route) => route.movementClass === 'GROUND').length
    if (groundReached === 1) anchorsSingleLane += 1
    if (groundReached >= 2) anchorsMultiLane += 1
    if (ground.length > 0 && groundReached === ground.length) {
      anchorsAllLanes += 1
      premiumAnchors.push({ ...anchor })
      if (air.length === 0 || reached.length === routes.length) anchorsAllRoutes += 1
    }
  }

  return {
    id: map.id,
    name: map.name,
    width: map.width,
    height: map.height,
    pathTiles,
    buildableCells,
    groundRoutes: ground.length,
    airRoutes: air.length,
    routes: routes.map((route) => ({
      id: route.id,
      movementClass: route.movementClass,
      spawn: { ...route.spawn },
      exit: { ...route.exit },
      cells: route.cells.length,
      length: routeTrack(route).totalLength,
      turns: countTurns(route.cells)
    })),
    coverage: {
      range,
      anchors: anchors.length,
      anchorsPerRoute,
      anchorsSingleLane,
      anchorsMultiLane,
      anchorsAllLanes,
      anchorsAllRoutes,
      premiumAnchors
    }
  }
}

/** One-line-per-route text block for reports and console output. */
export function formatLaneMetrics(metrics: LaneMapMetrics): string {
  const lines: string[] = []
  lines.push(
    `${metrics.id} (${metrics.name}) ${metrics.width}×${metrics.height} — ` +
      `${metrics.groundRoutes} ground + ${metrics.airRoutes} air, ` +
      `path ${metrics.pathTiles}, buildable ${metrics.buildableCells}, ` +
      `2×2 anchors ${metrics.coverage.anchors}`
  )
  for (const route of metrics.routes) {
    lines.push(
      `  ${route.id.padEnd(9)} ${route.movementClass.padEnd(6)} ` +
        `cells ${String(route.cells).padStart(3)} ` +
        `length ${route.length.toFixed(1).padStart(5)} ` +
        `turns ${String(route.turns).padStart(2)} ` +
        `spawn ${route.spawn.x},${route.spawn.y} → exit ${route.exit.x},${route.exit.y} ` +
        `| anchors in range ${metrics.coverage.anchorsPerRoute[route.id] ?? 0}`
    )
  }
  lines.push(
    `  coverage@${metrics.coverage.range}: single-lane ${metrics.coverage.anchorsSingleLane}, ` +
      `multi-lane ${metrics.coverage.anchorsMultiLane}, ` +
      `all-lanes ${metrics.coverage.anchorsAllLanes}, ` +
      `all-routes ${metrics.coverage.anchorsAllRoutes}`
  )
  return lines.join('\n')
}
